<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AttachmentsAPI from 'dashboard/api/attachments';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

const attachment = computed(() => props.attachment);
const { t } = useI18n();
const isRetrying = ref(false);

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.value.dataUrl);
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  downloadFile({ url: dataUrl, type: fileType, extension });
};

const hasTranscriptionData = computed(
  () => attachment.value.transcribedText && props.showTranscribedText
);
const hasTranslationData = computed(() => attachment.value.translation);
const hasTranscriptionError = computed(
  () => attachment.value.transcriptionStatus === 'failed'
);
const isTranscriptionPending = computed(
  () => attachment.value.transcriptionStatus === 'pending'
);
const canRetryTranscription = computed(
  () =>
    attachment.value.transcriptionStatus !== 'pending' &&
    (hasTranscriptionError.value || !hasTranscriptionData.value)
);

const retryTranscription = async () => {
  if (!attachment.value.id) return;
  isRetrying.value = true;
  try {
    await AttachmentsAPI.retryTranscription(attachment.value.id);
    useAlert(t('AUDIO_BUBBLE.RETRY_QUEUED'));
  } catch {
    useAlert(t('AUDIO_BUBBLE.RETRY_FAILED'));
  } finally {
    isRetrying.value = false;
  }
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ `${formatTime(currentTime)} / ${formatTime(duration)}` }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <!-- Transcription Status -->
    <div
      v-if="isTranscriptionPending"
      class="flex items-center gap-2 p-2 text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg w-full"
    >
      <span class="i-lucide-loader-2 animate-spin size-3" />
      {{ $t('AUDIO_BUBBLE.STATUS_PENDING') }}
    </div>

    <div
      v-if="hasTranscriptionError"
      class="flex items-center justify-between p-2 text-xs text-n-ruby-11 bg-n-ruby-2 rounded-lg w-full"
    >
      <span>{{ $t('AUDIO_BUBBLE.STATUS_FAILED') }}</span>
      <button
        v-if="canRetryTranscription"
        class="text-xs font-medium underline"
        :disabled="isRetrying"
        @click="retryTranscription"
      >
        {{
          isRetrying
            ? $t('AUDIO_BUBBLE.RETRYING')
            : $t('AUDIO_BUBBLE.RETRY_ACTION')
        }}
      </button>
    </div>

    <!-- Transcription Text -->
    <div v-if="hasTranscriptionData" class="space-y-1 w-full">
      <div
        class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg break-words"
      >
        {{ attachment.transcribedText }}
      </div>
      <div
        v-if="hasTranslationData"
        class="text-n-slate-11 p-2 text-xs bg-n-alpha-1 rounded-lg break-words"
      >
        <span class="font-medium">{{ $t('AUDIO_BUBBLE.TRANSLATION') }}</span>
        {{ attachment.translation }}
        <span
          v-if="attachment.translationConfidenceScore"
          class="text-n-slate-10 ml-1"
        >
          {{
            `(${Math.round(Number(attachment.translationConfidenceScore))}%)`
          }}
        </span>
      </div>
    </div>

    <!-- Retry Button (when no transcription and no error) -->
    <button
      v-if="
        canRetryTranscription &&
        !hasTranscriptionError &&
        !isTranscriptionPending
      "
      class="text-xs text-n-slate-11 underline mt-1"
      :disabled="isRetrying"
      @click="retryTranscription"
    >
      {{
        isRetrying
          ? $t('AUDIO_BUBBLE.RETRYING')
          : $t('AUDIO_BUBBLE.RETRY_ACTION')
      }}
    </button>
  </div>
</template>
